import { createInstance } from '@ats/repositories';
import type { StatusTemplate } from '@helpers/formatter';
import { useException } from '@hooks/utils/useException';
import { useTemplate } from '@hooks/utils/useTemplate';
import {
  LogType,
  type AttendeeInfoDTO,
  type CompanyInfoDTO,
  type LogReqDTO,
  type LogResDTO,
  type LoginDTO,
} from '@interfaces/referrfer';

export function useReferrerAPI() {
  const { withLogger, alertError } = useException();
  const { withStatusTemplate } = useTemplate();

  const fetchCompanyInfo: (
    apiUrl: string,
  ) => Promise<StatusTemplate<CompanyInfoDTO>> = async (apiUrl: string) => {
    try {
      const axios = createInstance({
        baseURL: apiUrl,
      });

      const { data } = await axios.get<CompanyInfoDTO>(`/api/companyInfo`);

      return withStatusTemplate({ status: true, data });
    } catch (error) {
      withLogger(error);
      alertError(error);

      return withStatusTemplate({
        status: false,
        data: {
          companyName: '',
          managerName: '',
          managerEmail: '',
          managerPhone: '',
          loginLogo: '',
          loginBackground: '',
          loginColor: '',
          drmYn: false,
        },
      });
    }
  };

  const fetchAttendee: (
    props: LoginDTO & { apiUrl: string },
  ) => Promise<StatusTemplate<AttendeeInfoDTO>> = async ({
    name,
    certificationCode,
    apiUrl,
  }: LoginDTO & { apiUrl: string }) => {
    try {
      const axios = createInstance({
        baseURL: apiUrl,
      });

      const formData = new FormData();
      formData.append('name', name);
      formData.append('certificationCode', certificationCode);

      const { data } = await axios.postForm<AttendeeInfoDTO>(
        `/api/login`,
        formData,
      );

      return withStatusTemplate({ status: true, data });
    } catch (error) {
      withLogger(error);
      alertError(error);

      return withStatusTemplate({
        status: false,
        data: {
          applySector: null,
          displayNo: -1,
          name: '',
          recruitNoticeName: '',
        },
      });
    }
  };

  const fetchIntervieweeLog: (
    props: Omit<LogReqDTO, 'logType'> & { apiUrl: string },
  ) => Promise<StatusTemplate<LogResDTO>> = async ({
    companyUrl,
    name,
    certificationCode,
    apiUrl,
  }) => {
    try {
      const axios = createInstance({
        baseURL: apiUrl,
      });

      const formData = new FormData();
      formData.append('companyUrl', companyUrl);
      formData.append('name', name);
      formData.append('certificationCode', certificationCode);
      formData.append('logType', LogType.INTERVIEWEE_PREPARED);

      const { data } = await axios.postForm<LogResDTO>(
        `/api/log/check`,
        formData,
      );

      return withStatusTemplate({ status: true, data });
    } catch (error) {
      withLogger(error);
      alertError(error);

      return withStatusTemplate({ status: true, data: false });
    }
  };

  const fetchInterviewerLog: (
    props: Omit<LogReqDTO, 'logType'> & { apiUrl: string },
  ) => Promise<StatusTemplate<LogResDTO>> = async ({
    certificationCode,
    companyUrl,
    name,
    apiUrl,
  }) => {
    try {
      const axios = createInstance({
        baseURL: apiUrl,
      });

      const formData = new FormData();
      formData.append('companyUrl', companyUrl);
      formData.append('name', name);
      formData.append('certificationCode', certificationCode);
      formData.append('logType', LogType.INTERVIEWER_PREPARED);

      const { data } = await axios.postForm<LogResDTO>(
        `/api/log/check`,
        formData,
      );

      return withStatusTemplate({ status: true, data });
    } catch (error) {
      withLogger(error);
      alertError(error);

      return withStatusTemplate({ status: true, data: false });
    }
  };

  const fetchInterviewerCreateLog: (
    props: Omit<LogReqDTO, 'logType' | 'name'> & {
      apiUrl: string;
      intervieweeName: string;
    },
  ) => Promise<StatusTemplate<LogResDTO>> = async ({
    certificationCode,
    companyUrl,
    intervieweeName,
    apiUrl,
  }) => {
    try {
      const axios = createInstance({
        baseURL: apiUrl,
      });

      const formData = new FormData();
      formData.append('companyUrl', companyUrl);
      formData.append('name', intervieweeName);
      formData.append('certificationCode', certificationCode);
      formData.append('logType', LogType.INTERVIEWER_PREPARED);

      const { data } = await axios.postForm<LogResDTO>(
        `/api/log/create`,
        formData,
      );

      return withStatusTemplate({ status: true, data });
    } catch (error) {
      withLogger(error);
      alertError(error);

      return withStatusTemplate({ status: true, data: false });
    }
  };

  const fetchIntervieweeCreateLog: (
    props: Omit<LogReqDTO, 'logType'> & {
      apiUrl: string;
    },
  ) => Promise<StatusTemplate<LogResDTO>> = async ({
    certificationCode,
    companyUrl,
    apiUrl,
    name,
  }) => {
    try {
      const axios = createInstance({
        baseURL: apiUrl,
      });

      const formData = new FormData();
      formData.append('companyUrl', companyUrl);
      formData.append('name', name);
      formData.append('certificationCode', certificationCode);
      formData.append('logType', LogType.INTERVIEWEE_PREPARED);

      const { data } = await axios.postForm<LogResDTO>(
        `/api/log/create`,
        formData,
      );

      return withStatusTemplate({ status: true, data });
    } catch (error) {
      withLogger(error);
      alertError(error);

      return withStatusTemplate({ status: true, data: false });
    }
  };

  return {
    fetchCompanyInfo,
    fetchAttendee,
    fetchIntervieweeLog,
    fetchInterviewerLog,
    fetchInterviewerCreateLog,
    fetchIntervieweeCreateLog,
  };
}
